import type { Metadata } from "next";
import { IsProduction } from "@/config/frontend";
import { getLanguageAlternates, getOgLocale } from "@/lib/seo/alternates";

const SITE_URL = "https://www.vell.net";
const SITE_NAME = "Vell";
const DEFAULT_LOCALE = "en_AU";
const DEFAULT_OG_IMAGE = `${SITE_URL}/share_landscape.png`; 

interface BuildMetadataOptions {
  title: string;
  description: string;
  path: string; 
  image?: string; 
  noIndex?: boolean; 
}

export function buildMetadata({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  noIndex = false,
}: BuildMetadataOptions): Metadata {
  const url = `${SITE_URL}${path}`;
  // Looked up from the page's own canonical rather than passed in, so a page
  // gains its hreflang annotations by being listed in lib/seo/alternates.ts
  // and nothing has to be remembered at the call site.
  const languages = getLanguageAlternates(path);

  return {
    title,
    description,
    alternates: { canonical: path, ...(languages ? { languages } : {}) },
    robots: !IsProduction
      ? { index: false, follow: false, googleBot: "noindex, nofollow" }
      : noIndex
        ? { index: false, follow: true }
        : { index: true, follow: true },
    openGraph: {
      title,
      description,
      url,
      siteName: SITE_NAME,
      type: "website",
      locale: getOgLocale(path) ?? DEFAULT_LOCALE,
      images: [{ url: image, width: 1200, height: 630, alt: title }],
    },
    twitter: {
      card: "summary_large_image", 
      title,
      description,
      images: [image],
    },
  };
}

export { SITE_URL, SITE_NAME };